import { FunctionComponent, useMemo } from "react";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { MsdynamicsPipeline, MsdynamicsPipelineKind } from "../schema/dynamics";
import { pipelineKindOf, resolveSelectableStageId } from "./dynamicsPipeline.utils";

type Ts = (key: string) => string;

interface DynamicsPipelinePickerProps {
	pipelines: MsdynamicsPipeline[];
	pipelineId: string | null;
	stageId: string | null;
	onChange: (next: { pipelineId: string | null; stageId: string | null }) => void;
	ts: Ts;
}

/**
 * The opportunity pipeline + stage pair.
 *
 * `GET pipelines` returns either the environment's real opportunity Business Process Flows
 * or — when it runs none — a single *virtual* pipeline built from the `salesstagecode`
 * option set. The two id spaces cannot be mixed, so switching the pipeline always clears a
 * stage that came from the other one, and a `SALES_STAGE` pipeline is labelled as such:
 * "no business process flow" is a fact about the customer's org, not an error.
 */
export const DynamicsPipelinePicker: FunctionComponent<DynamicsPipelinePickerProps> = ({
	pipelines,
	pipelineId,
	stageId,
	onChange,
	ts,
}) => {
	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelines.map((pipeline) => ({ id: pipeline.id, title: pipeline.label })),
		[pipelines]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		const selected = pipelines.find((pipeline) => pipeline.id === pipelineId);

		return (selected?.stages ?? []).map((stage) => ({ id: stage.id, title: stage.label }));
	}, [pipelines, pipelineId]);

	// A stage from the other id space (or from a pipeline that has since disappeared) is
	// shown as unset rather than as a selection the backend would silently drop.
	const selectableStageId = resolveSelectableStageId(pipelines, pipelineId, stageId);
	const isSalesStagePipeline = pipelineKindOf(pipelineId) === MsdynamicsPipelineKind.SALES_STAGE;

	return (
		<>
			<div className="flex flex-col gap-1">
				<Dropdown
					data={pipelineOptions}
					defaultValue={pipelineOptions.find((option) => option.id === pipelineId)}
					floating={true}
					handleSelect={(value) => onChange({ pipelineId: value.id as string, stageId: null })}
					label={ts("defaultPipeline")}
				/>
				{isSalesStagePipeline && (
					<span className="text-xs text-text-meta" data-testid="dynamics-sales-stage-note">
						{ts("salesStagePipelineNote")}
					</span>
				)}
			</div>

			<Dropdown
				key={pipelineId ?? "no-pipeline"}
				data={stageOptions}
				defaultValue={stageOptions.find((option) => option.id === selectableStageId)}
				disabled={!pipelineId}
				floating={true}
				handleSelect={(value) => onChange({ pipelineId, stageId: value.id as string })}
				label={ts("defaultStage")}
			/>
		</>
	);
};
